import { PlatformIcon } from '../profile/links';
import { isListeningTo, stopListening } from './listening';
import type { Room } from './api';

/**
 * Where the conversation actually is, when the host is simulcasting.
 *
 * ## Why the room chat goes away entirely
 *
 * A host streaming to Twitch or TikTok is reading *that* chat. Every question
 * they answer, every shout-out, every request is happening there. Leaving our
 * own chat box open beside it does two bad things at once: it splits the
 * audience between a busy room and an empty one, and it shows every listener a
 * dead panel with nobody in it - which reads as "nobody is here" about a
 * broadcast that may have hundreds of people in it somewhere else.
 *
 * So when a simulcast is set, this takes the whole column and points at it.
 *
 * ## It stops the audio on the way out
 *
 * Somebody who opens the stream is about to hear the same set from two places,
 * a second or two apart, which is genuinely unpleasant. The button says what it
 * will do rather than doing it silently - and it only says it when they are
 * actually listening here, because otherwise it is a promise about nothing.
 */
export function WatchElsewhere({ room }: { room: Room }) {
  const simulcast = room.simulcast;

  if (!simulcast) return null;

  const listening = isListeningTo(room.username);

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-5 px-6 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-[var(--radius-panel)] border border-hot-edge bg-hot-wash text-hot-text">
        <PlatformIcon platform={simulcast.platform} className="h-6 w-6" />
      </span>

      <div className="flex max-w-xs flex-col gap-2">
        <h3 className="text-lg font-bold tracking-tight text-ink">
          The chat is on {simulcast.label}
        </h3>
        <p className="text-sm leading-relaxed text-soft">
          {room.host.display_name} is streaming this set there. Requests, shout-outs and everybody
          talking about it are happening on {simulcast.label} rather than here.
        </p>
      </div>

      <a
        href={simulcast.url}
        target="_blank"
        /* User-chosen destination, opened in its own tab and denied a handle on
           this one. The server pinned the URL to the platform's own hosts. */
        rel="noopener noreferrer nofollow"
        data-watch-elsewhere={simulcast.platform}
        onClick={() => {
          // Two copies of the same set a second apart is unpleasant enough to
          // be worth acting on rather than warning about.
          if (listening) stopListening();
        }}
        className="inline-flex items-center gap-2 rounded-[var(--radius-pill)] border border-hot-edge bg-hot-wash px-5 py-2.5 text-sm font-medium text-hot-text transition-opacity hover:opacity-85"
      >
        <PlatformIcon platform={simulcast.platform} className="h-4 w-4" />
        Watch on {simulcast.label}
      </a>

      {listening && (
        <p className="font-mono text-[10px] leading-relaxed text-ghost">
          this stops the audio here, so you do not hear it twice
        </p>
      )}
    </div>
  );
}
